import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { AGGREGATE_NAMES, TEMPLATE_RESOLVERS } from 'cpool-constants';
import { schemaTemplateResolver_GetOwnMailHeaderFooter } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuMail } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { EmailComposer } from './EmailComposer';
import { useMailTemplates } from './hooks/useMailTemplates';
import { TemplateList } from './TemplateList';
import useLead from '../leadDetails/hooks/useLead';
import { useOfferTemplateList } from '../templates/hooks/useOfferTemplateList';
import { fetcher } from '~/queryUtils/useQuery.ts';
import { cn } from '~/utils/cnTailwind';

type Props = {
    leadId?: string;
    children?: React.ReactNode;
    initialOfferId?: string;
    buttonClassName?: string;
    onClose?: () => void;
};

export const EmailMessageDialog: React.FC<Props> = ({
    buttonClassName,
    children,
    initialOfferId,
    leadId: givenLeadId,
    onClose,
}) => {
    const params = useParams({ strict: false });
    let leadId: string | undefined;
    if (params && 'leadId' in params) {
        leadId = params.leadId;
    }

    const { data: lead } = useLead(leadId ?? givenLeadId ?? '');
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { data: templateList, isError, isLoading, isSuccess } = useMailTemplates();
    const {
        data: offerTemplateList,
        isError: isOfferTemplatesError,
        isLoading: isOfferTemplatesLoading,
    } = useOfferTemplateList();
    const queryClient = useQueryClient();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(templateList?.[0]?.id);
    const initialFocusRef = useRef<HTMLDivElement>(null);
    const hasHandledInitialOfferRef = useRef(false);

    const closeDialog = () => {
        setOpen(false);
        onClose?.();
    };

    const allTemplates = useMemo(() => {
        return [...(templateList ?? []), ...(offerTemplateList ?? [])];
    }, [templateList, offerTemplateList]);

    const onOpenDialog = useCallback(() => {
        if (isLoading) {
            toast.warning(t('MESSAGE.EMAIL.LOADING_TEMPLATES'));
            return;
        }
        if (isError) {
            toast.error(t('MESSAGE.EMAIL.LOADING_TEMPLATES_FAILED'));
            return;
        }
        if (isSuccess && allTemplates.length === 0) {
            toast.info(t('MESSAGE.EMAIL.NO_TEMPLATES'));
            return;
        }
        if (allTemplates && !selectedTemplateId) {
            setSelectedTemplateId(allTemplates[0].id);
        }
        setOpen(true);
    }, [allTemplates, isError, isLoading, isSuccess, selectedTemplateId, t]);

    // Offer flow auto-opens once with the first offer template; no fallback to mail templates
    useEffect(() => {
        if (!initialOfferId || hasHandledInitialOfferRef.current || isOfferTemplatesLoading) {
            return;
        }
        hasHandledInitialOfferRef.current = true;
        if (isOfferTemplatesError) {
            toast.error(t('TEMPLATES.INFO_MESSAGES.LOADING_TEMPLATES_FAILED'));
            onClose?.();
            return;
        }
        if (!offerTemplateList?.length) {
            toast.info(t('MESSAGE.EMAIL.NO_OFFER_TEMPLATES'));
            onClose?.();
            return;
        }
        setSelectedTemplateId(offerTemplateList[0].id);
        setOpen(true);
    }, [initialOfferId, isOfferTemplatesError, isOfferTemplatesLoading, offerTemplateList, onClose, t]);

    useEffect(() => {
        queryClient.prefetchQuery({
            queryFn: () =>
                fetcher({
                    meta: {
                        queryUrl: `/query/${AGGREGATE_NAMES.TEMPLATE}/${TEMPLATE_RESOLVERS.getOwnMailHeaderFooter}`,
                        schema: schemaTemplateResolver_GetOwnMailHeaderFooter,
                    },
                }),
            queryKey: [AGGREGATE_NAMES.TEMPLATE, TEMPLATE_RESOLVERS.getOwnMailHeaderFooter, {}],
        });
    }, [queryClient]);

    return (
        <>
            <button
                onClick={() => {
                    onOpenDialog();
                }}
                type="button"
                className={cn('inline-flex items-center px-1.5 py-1.5 space-x-2 text-xs', buttonClassName)}
            >
                {children || (
                    <>
                        <LuMail aria-hidden="true" data-testid="mail-icon" />
                        <span>{t('LEADS.DETAILS.CONTACT_BAR.MAIL')}</span>
                    </>
                )}
            </button>
            {lead && allTemplates && allTemplates.length > 0 && (
                <Modal
                    title={<span className="text-xl font-medium">{t('MESSAGE.EMAIL.TITLE')}</span>}
                    open={open}
                    onClose={closeDialog}
                    initialFocusRef={initialFocusRef}
                    panelClassName="max-w-[1320px] [&>div:first-of-type]:pt-5 [&>div:first-of-type>h6]:mb-2 [&>[data-testid=modal-body]]:flex [&>[data-testid=modal-body]]:flex-col [&>[data-testid=modal-body]]:overflow-hidden"
                >
                    <div className="flex min-h-0 flex-1 flex-col">
                        <EmailComposer
                            lead={lead}
                            selectedTemplateId={selectedTemplateId}
                            initialOfferId={initialOfferId}
                            onCancel={closeDialog}
                            onSuccess={closeDialog}
                            sidebar={
                                <TemplateList
                                    selectedTemplateId={selectedTemplateId}
                                    setSelectedTemplateId={setSelectedTemplateId}
                                    templates={allTemplates}
                                />
                            }
                        />
                    </div>
                </Modal>
            )}
        </>
    );
};
